import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AgGridTable from '../../../components/AgGridTable';
import SidebarPanel from '../../../components/admin/SidebarPanel';
import EducationalTooltip from '../../../components/shared/EducationalTooltip';
import RelationshipBadge from '../../../components/shared/RelationshipBadge';
import ModelDocumentationModal from '../../../components/shared/ModelDocumentationModal';
import EditableUserDetails from '../../../components/admin/EditableUserDetails';
import AlertMessage from '../../../components/shared/alertMessage';
import AuditLogs from '../../../components/shared/AuditLogs';
import PasswordInput from '../../../components/shared/PasswordInput';
import { adminApi } from '../../../services/api';
import { validatePassword } from '../../../utils/passwordValidation';

const AnkrUsersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, tab } = useParams();
  const gridRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ department: '', role_id: '', is_active: 'true', search: '' });
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, errors: [] });
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', username: '', password: '',
    role_id: '', department: '', employee_id: '', phone: '', is_active: true
  });
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [passwordData, setPasswordData] = useState({ password: '', confirmPasswordChange: false });
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [filters]);

  useEffect(() => {
    if (location.state?.mode === 'create') {
      setIsCreateMode(true);
      setSelectedUser(null);
      setShowPanel(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (userId && users.length > 0) {
      const user = users.find(u => u.id == userId);
      if (user) handleRowClick({ data: user });
    }
  }, [userId, users]);

  // Sync tab parameter with activeTab state
  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    } else if (userId && !tab) {
      setActiveTab('overview');
    }
  }, [tab, userId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { user_type: 'ankr', ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)) };
      const res = await adminApi.get('/admin/users/', { params });
      setUsers(res.data?.users || []);
      const depts = [...new Set(res.data?.users.map(u => u.department).filter(Boolean))];
      setDepartments(depts);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await adminApi.get('/admin/roles/', { params: { category: 'ankr_user' } });
      setRoles(res.data?.roles || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const handleRowClick = useCallback((event) => {
    setIsCreateMode(false);
    setSelectedUser(event.data);
    setShowPanel(true);
    setActiveTab('overview');
    navigate(`/admin/users/ankr/${event.data.id}/overview`, { replace: true });
  }, [navigate]);

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    if (selectedUser) {
      navigate(`/admin/users/ankr/${selectedUser.id}/${newTab}`, { replace: true });
    }
  }, [navigate, selectedUser]);

  const handleClosePanel = () => {
    setShowPanel(false);
    setSelectedUser(null);
    setIsCreateMode(false);
    setFormData({
      first_name: '', last_name: '', email: '', username: '', password: '',
      role_id: '', department: '', employee_id: '', phone: '', is_active: true
    });
    setPasswordData({ password: '', confirmPasswordChange: false });
    setAlertState({ show: false, type: 'info', title: '', message: '' });
    navigate('/admin/users/ankr', { replace: true });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setAlertState({ show: false, type: 'info', title: '', message: '' }); // Clear previous alerts

    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setAlertState({ show: true, type: 'error', title: 'Validation Error', message: passwordValidation.error });
      return;
    }

    try {
      // Convert empty strings to null for UUID fields
      const payload = {
        ...formData,
        user_type: 'ankr',
        role_id: formData.role_id || null
      };
      await adminApi.post('/admin/users/', payload);
      fetchUsers();
      setAlertState({show: true, type: 'success', message: 'Ankr user created successfully'});

      // Close panel after a short delay to show success message
      setTimeout(() => {
        handleClosePanel();
      }, 3000);
    } catch (err) {
      setAlertState({show: true, type: 'error', title: 'Failed to create user',
        message: err.response?.data?.message || err.message
      });
    }
  };

  const handleSavePassword = async () => {
    try {
      setSavingPassword(true);
      setAlertState({ show: false, type: 'info', title: '', message: '' });

      // Validate password
      if (passwordData.password && passwordData.password.trim() !== '') {
        const passwordValidation = validatePassword(passwordData.password);
        if (!passwordValidation.isValid) {
          setAlertState({ show: true, type: 'error', title: 'Validation Error', message: passwordValidation.error });
          setSavingPassword(false);
          return;
        }

        if (!passwordData.confirmPasswordChange) {
          setAlertState({ show: true, type: 'error', title: 'Confirmation Required', message: 'Please confirm the password change by checking the confirmation box' });
          setSavingPassword(false);
          return;
        }

        // Update password
        const response = await adminApi.put(`/admin/users/ankr/${selectedUser.id}/`, { password: passwordData.password });

        if (response.data?.success) {
          setAlertState({ show: true, type: 'success', title: 'Password updated successfully', message: '' });
          setPasswordData({ password: '', confirmPasswordChange: false });
        } else {
          throw new Error(response.data?.message || 'Failed to update password');
        }
      }
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to update password', message: err.response?.data?.message || err.message });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCellValueChanged = async (event) => {
    try {
      const { data, colDef, newValue } = event;
      await adminApi.patch(`/admin/users/${data.id}/`, { [colDef.field]: newValue });
      fetchUsers();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.message || err.message));
      fetchUsers(); // Revert
    }
  };

  const handleExport = () => {
    gridRef.current?.api.exportDataAsCsv({ fileName: `ankr-users-${new Date().toISOString().split('T')[0]}.csv` });
  };

  const handleBulkImport = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.txt';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        return headers.reduce((obj, h, i) => ({ ...obj, [h]: values[i] }), {});
      });

      if (rows.length === 0) return alert('No data found in file');

      if (!confirm(`Import ${rows.length} users? This will create them one by one.`)) return;

      setImporting(true);
      setImportProgress({ current: 0, total: rows.length, errors: [] });

      for (let i = 0; i < rows.length; i++) {
        try {
          await adminApi.post('/admin/users/', { ...rows[i], user_type: 'ankr' });
          setImportProgress(prev => ({ ...prev, current: i + 1 }));
        } catch (err) {
          setImportProgress(prev => ({ ...prev, errors: [...prev.errors, { row: i + 1, error: err.response?.data?.message || err.message }] }));
        }
      }

      setImporting(false);
      fetchUsers();
      alert(`Import complete! Created ${rows.length - importProgress.errors.length}/${rows.length} users.`);
    };
    input.click();
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to deactivate ${user.first_name} ${user.last_name}? This will set their account to inactive.`)) {
      return;
    }

    try {
      const response = await adminApi.deleteUser(user.id);
      if (response.data.success) {
        fetchUsers();
        alert('User deactivated successfully');
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      alert(`Error deactivating user: ${error.response?.data?.message || error.message}`);
    }
  };

  const columnDefs = [
    { headerName: 'Name', valueGetter: p => `${p.data?.first_name || ''} ${p.data?.last_name || ''}`, width: 150 },
    { headerName: 'Email', field: 'email', width: 200, editable: true },
    { headerName: 'Username', field: 'username', width: 130 },
    {
      headerName: 'Role',
      field: 'role.name',
      width: 120,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: roles.map(r => r.name) }
    },
    {
      headerName: 'Department',
      field: 'department',
      width: 130,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: departments }
    },
    { headerName: 'Employee ID', field: 'employee_id', width: 110 },
    { headerName: 'Manager', valueGetter: p => p.data?.manager_name || '-', width: 130 },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: p => (
        <span className={`badge bg-${p.data?.is_active ? 'success' : 'danger'}`}>
          {p.data?.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      headerName: 'Actions',
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: p => {
        if (!p.data) return null;
        return (
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteUser(p.data);
              }}
              title="Deactivate user"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <>
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
          <h4 className="text-lg">
            Ankr Users
          </h4>
          <p className="text-muted">Internal Ankr employees who manage and operate the platform</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
              <li className="breadcrumb-item"><a href="">User Management</a></li>
              <li className="breadcrumb-item active">Ankr Users</li>
            </ol>
          </nav>
        </div>
      </div>
      <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
        <button className="btn btn-primary" onClick={() => navigate('/admin/users/ankr', { state: { mode: 'create' } })}>
          <i className="fas fa-plus me-1" />Add Ankr User
        </button>
      </div>
      <div className="card">
        <div className="d-flex justify-content-between mb-3 p-2">
          <div className='d-flex gap-2'>
            <select className="form-select form-select-sm" value={filters.department} onChange={e => setFilters({...filters, department: e.target.value})}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select className="form-select form-select-sm" value={filters.role_id} onChange={e => setFilters({...filters, role_id: e.target.value})}>
              <option value="">All Roles</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select className="form-select form-select-sm" value={filters.is_active} onChange={e => setFilters({...filters, is_active: e.target.value})}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <input type="text" className="form-control form-control-sm" placeholder="Search..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
            <vr></vr>
            <small className="text-muted text-nowrap align-self-center">
              Showing {users.length} user{users.length !== 1 ? 's' : ''}
            </small>
          </div>
          <div className='d-flex gap-2 justify-content-end'>
            <button className="btn btn-sm btn-info" onClick={() => setShowDocsModal(true)}>
              <i className="fas fa-book me-1" />Docs
            </button>
            <button className="btn btn-sm btn-success" onClick={handleExport}>
              <i className="fas fa-file-export me-1" />Export
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleBulkImport} disabled={importing}>
              <i className="fas fa-file-import me-1" />{importing ? `Importing ${importProgress.current}/${importProgress.total}...` : 'Bulk Import'}
            </button>
          </div>
        </div>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <AgGridTable
            ref={gridRef}
            rowData={users}
            columnDefs={columnDefs}
            onRowClicked={handleRowClick}
            onCellValueChanged={handleCellValueChanged}
            getRowStyle={params => params.node.isSelected() ? { backgroundColor: '#f0f8ff' } : null}
          />
        )}
      </div>

      <SidebarPanel isOpen={showPanel} onClose={handleClosePanel} title={isCreateMode ? "Create Ankr User" : "Ankr User Details"}>
        {isCreateMode ? (
          <div className="card">
            <div className="card-body p-3">
              <>
                {/* Alert Component */}
                {alertState.show && (
                  <AlertMessage
                    type={alertState.type}
                    title={alertState.title}
                    message={alertState.message}
                    dismissible={true}
                    onDismiss={() => setAlertState({ ...alertState, show: false })}
                    className="mb-3"
                  />
                )}

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">First Name *</label>
                    <input type="text" className="form-control form-control-sm" required value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Last Name *</label>
                    <input type="text" className="form-control form-control-sm" required value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Email *</label>
                    <input type="email" className="form-control form-control-sm" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Username *</label>
                    <input type="text" className="form-control form-control-sm" required value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-12">
                    <PasswordInput
                      value={formData.password}
                      onChange={e => setFormData({...formData, password: e.target.value})}
                      required
                      label="Password"
                      showRequirements
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Role</label>
                    <select className="form-select form-select-sm" value={formData.role_id} onChange={e => setFormData({...formData, role_id: e.target.value})}>
                      <option value="">Select Role</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Department</label>
                    <input type="text" className="form-control form-control-sm" value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Employee ID</label>
                    <input type="text" className="form-control form-control-sm" value={formData.employee_id} onChange={e => setFormData({...formData, employee_id: e.target.value})} />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Phone</label>
                    <input type="tel" className="form-control form-control-sm" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Status</label>
                    <select className="form-select form-select-sm" value={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.value === 'true'})}>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12">
                    <button type="button" className="btn btn-primary btn-sm me-2" onClick={handleCreateUser}>Create User</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleClosePanel}>Cancel</button>
                  </div>
                </div>
              </>
            </div>
          </div>
        ) : selectedUser && (
          <>
            <ul className="nav nav-tabs nav-tabs-custom mb-2">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => handleTabChange('overview')}>Overview</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => handleTabChange('audit-logs')}>Audit Trail</button>
              </li>
            </ul>

            {activeTab === 'overview' && (
              <>
              <div className="card">
                <div className="card-body p-3">
                  {/* Alert Component */}
                  {alertState.show && (
                    <AlertMessage
                      type={alertState.type}
                      title={alertState.title}
                      message={alertState.message}
                      dismissible={true}
                      onDismiss={() => setAlertState({ ...alertState, show: false })}
                      className="mb-3"
                    />
                  )}

                  <EditableUserDetails
                    userData={{
                      ...selectedUser,
                      role_id: selectedUser.role?.id || null
                    }}
                    userId={selectedUser.id}
                    userType="ankr"
                    fieldConfig={[
                      { name: 'first_name', label: 'First Name', type: 'text', editable: true },
                      { name: 'last_name', label: 'Last Name', type: 'text', editable: true },
                      { name: 'email', label: 'Email', type: 'email', editable: true },
                      { name: 'username', label: 'Username', type: 'text', editable: false },
                      { name: 'role_id', label: 'Role', type: 'select', editable: true, options: roles.map(r => ({ value: r.id, label: r.name })) },
                      { name: 'department', label: 'Department', type: 'text', editable: true },
                      { name: 'employee_id', label: 'Employee ID', type: 'text', editable: true },
                      { name: 'phone', label: 'Phone', type: 'text', editable: true },
                      { name: 'is_active', label: 'Active', type: 'checkbox', editable: true },
                      { name: 'created_at', label: 'Created At', type: 'date', editable: false },
                    ]}
                    onSave={() => {
                      fetchUsers();
                      setAlertState({ show: true, type: 'success', title: 'User updated successfully', message: '' });
                    }}
                    onError={(err) => {
                      console.error('Error updating user:', err);
                    }}
                  />
                </div>
              </div>
              <div className="card mt-3">
                <div className="card-body p-3">
                  <h6 className="mb-3">Password Management</h6>
                  <div className="row">
                    <div className="col-12">
                      <PasswordInput
                        value={passwordData.password}
                        onChange={e => { setPasswordData({...passwordData, password: e.target.value, confirmPasswordChange: false}); }}
                        required={false}
                        label="New Password"
                        placeholder="Leave blank to keep current password"
                        showRequirements
                        className="mb-2"
                      />
                      {passwordData.password && passwordData.password.trim() !== '' && (
                        <div className="form-check mt-2 mb-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="confirmPasswordChangeAnkr"
                            checked={passwordData.confirmPasswordChange}
                            onChange={e => setPasswordData({...passwordData, confirmPasswordChange: e.target.checked})}
                          />
                          <label className="form-check-label small text-muted" htmlFor="confirmPasswordChangeAnkr">
                            I want to update the user&apos;s password. This will not affect their existing sessions. They can continue using Ankr till next login.
                          </label>
                        </div>
                      )}
                      {passwordData.password && passwordData.password.trim() !== '' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleSavePassword}
                          disabled={savingPassword}
                        >
                          {savingPassword ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1"></span>
                              Updating Password...
                            </>
                          ) : (
                            'Update Password'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
            )}

            {activeTab === 'audit-logs' && (
              <div className="card">
                <div className="card-body p-3">
                  <AuditLogs
                    resourceType="user"
                    userType="ankr"
                    resourceId={selectedUser.id}
                    title="User Audit Trail"
                    showHeader={true}
                    pageSize={25}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </SidebarPanel>

      <ModelDocumentationModal show={showDocsModal} onHide={() => setShowDocsModal(false)} modelType="ankr_user" />
    </>
  );
};

export default AnkrUsersPage;
